import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Separator } from "../components/ui/separator";
import { UserCircle, Plus, PencilSimple, Trash, MagnifyingGlass, Warning, CalendarBlank, Phone, Envelope, IdentificationCard, FilePdf, FileCsv, Crown, Star, Medal, TrendUp } from "@phosphor-icons/react";
import { useRestaurant } from "../contexts/RestaurantContext";

const TIER_CONFIG = {
  gold: { label: "Gold", bg: "#FEF3C7", color: "#B8860B", border: "#D4AF37", icon: Crown, iconColor: "#D4AF37" },
  silver: { label: "Silver", bg: "#F3F4F6", color: "#6B7280", border: "#A8A8A8", icon: Star, iconColor: "#A8A8A8" },
  bronze: { label: "Bronze", bg: "#FED7AA20", color: "#A0522D", border: "#CD7F32", icon: Medal, iconColor: "#CD7F32" },
};
const TierBadge = ({ tier }) => {
  if (!tier || !TIER_CONFIG[tier]) return null;
  const c = TIER_CONFIG[tier];
  return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border" style={{ backgroundColor: c.bg, color: c.color, borderColor: c.border }} data-testid={`tier-badge-${tier}`}>{c.label}</span>;
};

export default function MembersPage() {
  const { restId } = useRestaurant();
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberAnalytics, setMemberAnalytics] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", member_number: "", allergens: "", special_requests: "", notes: "" });
  const [memberTiers, setMemberTiers] = useState({});

  const fetchMembers = useCallback(async () => {
    try {
      let params = search ? `?search=${encodeURIComponent(search)}` : "";
      const { data } = await api.get(`/members${params}`);
      setMembers(data);
    } catch { toast.error("Failed to load members"); }
  }, [search]);

  const fetchMemberTiers = useCallback(async () => {
    try {
      const { data } = await api.get("/members/tiers");
      setMemberTiers(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);
  useEffect(() => { fetchMemberTiers(); }, [fetchMemberTiers]);

  const createMember = async () => {
    if (!form.name.trim()) return;
    try {
      await api.post("/members", { ...form, restaurant_id: restId });
      setShowAdd(false);
      resetForm();
      fetchMembers();
      toast.success("Member added");
    } catch { toast.error("Failed"); }
  };

  const updateMember = async () => {
    if (!editingMember) return;
    try {
      await api.put(`/members/${editingMember.id}`, form);
      setShowEdit(false);
      setEditingMember(null);
      fetchMembers();
      toast.success("Member updated");
    } catch { toast.error("Failed"); }
  };

  const deleteMember = async (id) => {
    try {
      await api.delete(`/members/${id}`);
      fetchMembers();
      toast.success("Member removed");
    } catch { toast.error("Failed"); }
  };

  const openEdit = (m) => {
    setEditingMember(m);
    setForm({ name: m.name, phone: m.phone || "", email: m.email || "", member_number: m.member_number || "", allergens: m.allergens || "", special_requests: m.special_requests || "", notes: m.notes || "" });
    setShowEdit(true);
  };

  const openDetails = async (m) => {
    try {
      const { data } = await api.get(`/members/${m.id}/analytics`);
      setMemberAnalytics(data);
      setShowDetails(true);
    } catch { toast.error("Failed to load analytics"); }
  };

  const resetForm = () => setForm({ name: "", phone: "", email: "", member_number: "", allergens: "", special_requests: "", notes: "" });

  const exportCSV = async () => {
    try {
      const response = await api.get("/export/members?format=csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "members_export.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const exportPDF = async () => {
    try {
      const { data: exportData } = await api.get("/export/members?format=json");
      const today = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
      const tierLabel = (t) => ({ gold: "Gold", silver: "Silver", bronze: "Bronze" }[t] || "—");
      const tierColor = (t) => ({ gold: "#B8860B", silver: "#6B7280", bronze: "#A0522D" }[t] || "#78716C");
      const rows = exportData.map((m) => `
        <tr>
          <td style="padding:8px;border-bottom:1px solid #E7E5E4;font-weight:500">${m.name}</td>
          <td style="padding:8px;border-bottom:1px solid #E7E5E4;color:#78716C">${m.member_number || "—"}</td>
          <td style="padding:8px;border-bottom:1px solid #E7E5E4;color:#78716C">${m.phone || "—"}</td>
          <td style="padding:8px;border-bottom:1px solid #E7E5E4;font-weight:600;color:${tierColor(m.tier)}">${tierLabel(m.tier)}</td>
          <td style="padding:8px;border-bottom:1px solid #E7E5E4;text-align:center">${m.visit_count || 0}</td>
          <td style="padding:8px;border-bottom:1px solid #E7E5E4;color:#78716C">${m.last_visit_date || "—"}</td>
          <td style="padding:8px;border-bottom:1px solid #E7E5E4;color:#DC2626;font-size:12px">${m.allergens || ""}</td>
        </tr>`).join("");
      const html = `<html><head><title>Members Export - ${today}</title>
        <style>body{font-family:'Helvetica Neue',Arial,sans-serif;max-width:900px;margin:30px auto;color:#1C1917;padding:20px;font-size:13px}h1{font-size:22px;color:#2F4F4F;margin-bottom:4px}.subtitle{color:#78716C;font-size:13px;margin-bottom:20px}table{width:100%;border-collapse:collapse}th{text-align:left;padding:8px;border-bottom:2px solid #D6D3D1;font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#78716C}.footer{margin-top:24px;text-align:center;font-size:10px;color:#A8A29E}@media print{body{margin:10px}}</style></head><body>
        <h1>Dining Floor Studio</h1><p class="subtitle">Members CRM — ${today} · ${exportData.length} members</p>
        <table><thead><tr><th>Name</th><th>Member #</th><th>Phone</th><th>Tier</th><th>Visits</th><th>Last Visit</th><th>Allergens</th></tr></thead><tbody>${rows}</tbody></table>
        <div class="footer">Generated by Dining Floor Studio</div></body></html>`;
      const win = window.open("", "_blank");
      win.document.write(html);
      win.document.close();
      setTimeout(() => { win.print(); }, 500);
    } catch { toast.error("Export failed"); }
  };

  const to12h = (t) => t || "";

  // Categorize members by tier
  const getMemberTier = (m) => memberTiers[m.id] || null;
  const topMember = members.length > 0 ? [...members].sort((a, b) => (b.visit_count || 0) - (a.visit_count || 0))[0] : null;
  const goldMembers = members.filter((m) => getMemberTier(m) === "gold");
  const silverMembers = members.filter((m) => getMemberTier(m) === "silver");
  const bronzeMembers = members.filter((m) => getMemberTier(m) === "bronze");
  const otherMembers = members.filter((m) => !getMemberTier(m));
  const filteredBySearch = search ? members : null;

  const MemberForm = ({ onSubmit, buttonLabel }) => (
    <div className="space-y-4 mt-2 max-h-[55vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-name-input" /></div>
        <div><Label className="text-xs">Member #</Label><Input value={form.member_number} onChange={(e) => setForm({ ...form, member_number: e.target.value })} data-testid="member-number-input" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="member-phone-input" /></div>
        <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-email-input" /></div>
      </div>
      <div><Label className="text-xs">Allergens</Label><Input value={form.allergens} onChange={(e) => setForm({ ...form, allergens: e.target.value })} placeholder="e.g., Peanuts, Shellfish" data-testid="member-allergens-input" /></div>
      <div><Label className="text-xs">Special Requests</Label><Input value={form.special_requests} onChange={(e) => setForm({ ...form, special_requests: e.target.value })} data-testid="member-requests-input" /></div>
      <div><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="member-notes-input" /></div>
      <Button className="w-full" style={{ backgroundColor: "#2F4F4F" }} onClick={onSubmit} data-testid="member-submit-btn">{buttonLabel}</Button>
    </div>
  );

  const MemberCard = ({ m, compact }) => (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-stone-100 hover:bg-stone-50 transition-all cursor-pointer group" onClick={() => openDetails(m)} data-testid={`member-card-${m.id}`}>
      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0" style={{ backgroundColor: getMemberTier(m) === "gold" ? "#D4AF37" : getMemberTier(m) === "silver" ? "#A8A8A8" : getMemberTier(m) === "bronze" ? "#CD7F32" : "#2F4F4F" }}>
        {m.name.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-stone-800 text-sm truncate">{m.name}</span>
          {m.member_number && <span className="text-[9px] text-stone-400">#{m.member_number}</span>}
          <TierBadge tier={getMemberTier(m)} />
        </div>
        <div className="flex items-center gap-2 text-[11px] text-stone-400 mt-0.5">
          {m.visit_count > 0 && <span>{m.visit_count} visit{m.visit_count !== 1 ? "s" : ""}</span>}
          {m.phone && <span>{m.phone}</span>}
          {m.last_visit && <span>Last: {new Date(m.last_visit).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
        </div>
        {m.allergens && <div className="flex items-center gap-1 mt-0.5"><Warning size={10} className="text-red-500" /><span className="text-[10px] text-red-500">{m.allergens}</span></div>}
      </div>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={(e) => { e.stopPropagation(); openEdit(m); }} className="text-stone-400 hover:text-stone-700 p-1"><PencilSimple size={13} /></button>
        <button onClick={(e) => { e.stopPropagation(); deleteMember(m.id); }} className="text-stone-400 hover:text-red-500 p-1"><Trash size={13} /></button>
      </div>
    </div>
  );

  const TierSection = ({ title, icon: Icon, iconColor, members: tierMembers, emptyText }) => {
    if (tierMembers.length === 0 && !emptyText) return null;
    return (
      <div className="mb-6" data-testid={`tier-section-${title.toLowerCase()}`}>
        <div className="flex items-center gap-2 mb-3">
          {Icon && <Icon size={18} weight="fill" style={{ color: iconColor }} />}
          <h3 className="text-sm font-semibold uppercase tracking-widest" style={{ color: iconColor || "#57534E" }}>{title}</h3>
          <span className="text-xs text-stone-400">({tierMembers.length})</span>
        </div>
        {tierMembers.length === 0 ? (
          <p className="text-xs text-stone-400 pl-7">{emptyText}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {tierMembers.map((m) => <MemberCard key={m.id} m={m} />)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="members-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-2">
          <UserCircle size={24} weight="duotone" style={{ color: "#2F4F4F" }} />
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Members</h2>
          <Badge className="bg-red-100 text-red-700 text-[10px]">Admin Only</Badge>
          <span className="text-xs text-stone-400 ml-1">{members.length} total</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="pl-8 h-8 text-xs w-40" data-testid="member-search" />
          </div>
          <Button onClick={() => { resetForm(); setShowAdd(true); }} style={{ backgroundColor: "#2F4F4F" }} size="sm" data-testid="add-member-btn" className="text-xs">
            <Plus size={14} className="mr-1" /> Add
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV} className="text-xs h-8" data-testid="export-csv-btn">
            <FileCsv size={14} className="mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={exportPDF} className="text-xs h-8" data-testid="export-pdf-btn">
            <FilePdf size={14} className="mr-1" /> PDF
          </Button>
        </div>
      </div>

      {members.length === 0 ? (
        <div className="text-center py-16 animate-fade-in">
          <UserCircle size={48} className="mx-auto mb-3 text-stone-300" weight="duotone" />
          <p className="text-stone-500 text-sm">{search ? "No members found" : "No members yet"}</p>
          <p className="text-xs text-stone-400 mt-1">Members are automatically saved from reservations and walk-ins at end of service</p>
        </div>
      ) : search ? (
        /* Search results - flat list */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {members.map((m) => <MemberCard key={m.id} m={m} />)}
        </div>
      ) : (
        <>
          {/* Top Member Spotlight */}
          {topMember && topMember.visit_count > 1 && (
            <div className="mb-6 p-5 rounded-xl border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-amber-50/30" data-testid="top-member-spotlight">
              <div className="flex items-center gap-1.5 mb-3">
                <Crown size={18} weight="fill" className="text-amber-500" />
                <span className="text-xs font-bold uppercase tracking-widest text-amber-700">Top Member</span>
              </div>
              <div className="flex items-center gap-4 cursor-pointer" onClick={() => openDetails(topMember)}>
                <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl" style={{ backgroundColor: "#D4AF37" }}>
                  {topMember.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: "Work Sans" }}>{topMember.name}</h3>
                  <div className="flex items-center gap-3 text-xs text-stone-500 mt-0.5">
                    <span className="font-semibold text-amber-700">{topMember.visit_count} visits</span>
                    {topMember.member_number && <span>#{topMember.member_number}</span>}
                    {topMember.phone && <span>{topMember.phone}</span>}
                    <TierBadge tier={getMemberTier(topMember)} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tier Sections */}
          <TierSection title="Gold Members" icon={Crown} iconColor="#D4AF37" members={goldMembers} emptyText="5+ visits in 7 days to earn Gold" />
          <TierSection title="Silver Members" icon={Star} iconColor="#A8A8A8" members={silverMembers} emptyText="3+ visits in 7 days to earn Silver" />
          <TierSection title="Bronze Members" icon={Medal} iconColor="#CD7F32" members={bronzeMembers} emptyText="2+ visits in 7 days to earn Bronze" />

          {/* All Other Members */}
          <div className="mb-6" data-testid="tier-section-all">
            <div className="flex items-center gap-2 mb-3">
              <UserCircle size={18} weight="duotone" className="text-stone-400" />
              <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-400">All Members</h3>
              <span className="text-xs text-stone-400">({otherMembers.length})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {otherMembers.map((m) => <MemberCard key={m.id} m={m} />)}
            </div>
          </div>
        </>
      )}

      {/* Add Member */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-member-dialog">
          <DialogHeader><DialogTitle>New Member</DialogTitle></DialogHeader>
          <MemberForm onSubmit={createMember} buttonLabel="Add Member" />
        </DialogContent>
      </Dialog>

      {/* Edit Member */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent data-testid="edit-member-dialog">
          <DialogHeader><DialogTitle>Edit Member</DialogTitle></DialogHeader>
          <MemberForm onSubmit={updateMember} buttonLabel="Save Changes" />
        </DialogContent>
      </Dialog>

      {/* Member Details/Analytics */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-lg" data-testid="member-details-dialog">
          <DialogHeader>
            <DialogTitle>{memberAnalytics?.name} — Details</DialogTitle>
          </DialogHeader>
          {memberAnalytics && (
            <div className="mt-2 space-y-4">
              <div className="flex items-center gap-3 text-xs text-stone-500 flex-wrap">
                {memberAnalytics.member_number && <span className="flex items-center gap-1"><IdentificationCard size={12} />#{memberAnalytics.member_number}</span>}
                {memberAnalytics.phone && <span className="flex items-center gap-1"><Phone size={12} />{memberAnalytics.phone}</span>}
                {memberAnalytics.email && <span className="flex items-center gap-1"><Envelope size={12} />{memberAnalytics.email}</span>}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-stone-50 text-center">
                  <p className="text-[10px] text-stone-500 uppercase">Total Visits</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Work Sans", color: "#2F4F4F" }}>{memberAnalytics.total_visits}</p>
                </div>
                <div className="p-3 rounded-lg bg-stone-50 text-center">
                  <p className="text-[10px] text-stone-500 uppercase">Last 7 Days</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Work Sans", color: "#81B29A" }}>{memberAnalytics.visits_7d}</p>
                </div>
                <div className="p-3 rounded-lg bg-stone-50 text-center">
                  <p className="text-[10px] text-stone-500 uppercase">Last 30 Days</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "Work Sans", color: "#F4A261" }}>{memberAnalytics.visits_30d}</p>
                </div>
              </div>

              {/* Tier Progress */}
              <div className="p-3 rounded-lg bg-stone-50 border border-stone-200">
                <p className="text-xs font-semibold text-stone-600 mb-2 flex items-center gap-1"><TrendUp size={14} /> Tier Progress (7-day visits)</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 rounded-full bg-stone-200 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{
                      width: `${Math.min(100, (memberAnalytics.visits_7d / 5) * 100)}%`,
                      backgroundColor: memberAnalytics.visits_7d >= 5 ? "#D4AF37" : memberAnalytics.visits_7d >= 3 ? "#A8A8A8" : memberAnalytics.visits_7d >= 2 ? "#CD7F32" : "#D6D3D1",
                    }} />
                  </div>
                  <span className="text-xs font-mono text-stone-500">{memberAnalytics.visits_7d}/5</span>
                </div>
                <div className="flex justify-between text-[9px] text-stone-400 mt-1 px-0.5">
                  <span>Bronze (2)</span>
                  <span>Silver (3)</span>
                  <span>Gold (5)</span>
                </div>
              </div>

              {memberAnalytics.allergens && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-xs font-semibold text-red-700 flex items-center gap-1"><Warning size={14} /> Allergens</p>
                  <p className="text-sm text-red-600 mt-1">{memberAnalytics.allergens}</p>
                </div>
              )}
              {memberAnalytics.special_requests && (
                <div className="p-3 rounded-lg bg-stone-50 border border-stone-200">
                  <p className="text-xs font-semibold text-stone-600">Special Requests</p>
                  <p className="text-sm text-stone-700 mt-1">{memberAnalytics.special_requests}</p>
                </div>
              )}
              {memberAnalytics.notes && <p className="text-xs text-stone-500">{memberAnalytics.notes}</p>}

              <Separator />

              <div>
                <h4 className="text-xs font-semibold uppercase tracking-widest text-stone-500 mb-2">Recent Reservations</h4>
                {memberAnalytics.reservations?.length === 0 ? (
                  <p className="text-xs text-stone-500 py-2">No reservations found</p>
                ) : (
                  <div className="max-h-[200px] overflow-y-auto space-y-1.5">
                    {memberAnalytics.reservations?.map((r) => (
                      <div key={r.id} className="flex items-center justify-between p-2.5 rounded-lg border border-stone-100 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-stone-700">{r.date}</span>
                          <span className="text-stone-500">{to12h(r.time)}</span>
                          <span className="text-stone-500">Party of {r.party_size}</span>
                        </div>
                        <Badge className={`text-[9px] ${r.status === "completed" ? "bg-green-100 text-green-700" : r.status === "cancelled" ? "bg-red-100 text-red-600" : r.status === "seated" ? "bg-blue-100 text-blue-700" : "bg-stone-100 text-stone-500"}`}>
                          {r.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
